import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  BRAND_KIT_COPY as C,
  BRAND_KIT_DECISION_ERRORS,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import type { FactoryAssetSet } from '@/lib/types/AppFactory';
import { ApiError } from '@/shared/api/ApiError';
import { BrandKitUtils } from './BrandKitUtils';
import { useDecideAssetSet } from './useAppFactory';

/** One pending draft with the image set it belongs to. */
export interface AssetSetDraft {
  scope: FactoryAssetPlatform;
  set: FactoryAssetSet;
}

/**
 * The decisions on image-set drafts, with their confirmations and notices —
 * shared by the Brand Kit tab (approve all, discard all) and a platform's
 * Images tab (one upload draft). `tv` = the TV platforms that are on, so every
 * sentence names only those.
 */
export function useAssetSetDecisions(appCfgId: number, tv: readonly TvPlatformKey[]) {
  const mutation = useDecideAssetSet(appCfgId);

  const fail = (err: unknown) =>
    BrandKitUtils.isStillWorking(err)
      ? notifications.show({ title: C.decisionSlowTitle, message: C.decisionSlowMsg, color: 'blue' })
      : notifications.show({
      title: 'Error',
      message:
        err instanceof ApiError
          ? BrandKitUtils.refusalMessage(err, BRAND_KIT_DECISION_ERRORS)
          : err instanceof Error
            ? err.message
            : 'Something went wrong.',
      color: 'red',
    });

  const published = () => notifications.show({ title: C.publishedTitle, message: C.publishedMsg, color: 'green' });

  const approve = (draft: AssetSetDraft) => {
    modals.openConfirmModal({
      title: C.approveAllTitle,
      children: <Text size="sm">{C.approveAllBody.replace('{platforms}', BrandKitUtils.scopeLabel(draft.scope, tv))}</Text>,
      labels: { confirm: C.approveOne, cancel: 'Cancel' },
      onConfirm: () =>
        mutation.mutate({ assetSetId: draft.set.assetSetId, state: 'approved' }, { onSuccess: published, onError: fail }),
    });
  };

  const override = (draft: AssetSetDraft, reason: string) => {
    mutation.mutate(
      { assetSetId: draft.set.assetSetId, state: 'approved', overrideReason: reason },
      { onSuccess: published, onError: fail },
    );
  };

  const discard = (draft: AssetSetDraft) => {
    mutation.mutate(
      { assetSetId: draft.set.assetSetId, state: 'rejected' },
      {
        onSuccess: () => notifications.show({ title: C.discardedTitle, message: C.discardedMsg, color: 'gray' }),
        onError: fail,
      },
    );
  };

  /** One `kit` decision per Brand Kit revision among the drafts (normally one). */
  const decideAll = async (drafts: readonly AssetSetDraft[], state: 'approved' | 'rejected') => {
    const approved: FactoryAssetPlatform[] = [];
    let blocked = 0;
    for (const group of BrandKitUtils.kitGroups(drafts)) {
      try {
        const res = await mutation.mutateAsync({ assetSetId: group.assetSetId, state, kit: true });
        const stillBlocked = new Set((res.blocked ?? []).map((b) => b.platform));
        blocked += stillBlocked.size;
        approved.push(...group.scopes.filter((scope) => !stillBlocked.has(scope)));
      } catch (err) {
        // Nothing of this revision could be approved: its drafts all stay.
        if (err instanceof ApiError && err.code === 'quality_gate_blocked') {
          blocked += group.scopes.length;
          continue;
        }
        fail(err);
        return;
      }
    }
    if (state === 'rejected') {
      notifications.show({ title: C.discardedTitle, message: C.discardedMsg, color: 'gray' });
    } else if (blocked > 0) {
      notifications.show({
        title: C.publishedPartTitle,
        message:
          approved.length > 0
            ? C.publishedPartMsg
                .replace('{done}', BrandKitUtils.joinLabels(approved.map((s) => BrandKitUtils.scopeLabel(s, tv))))
                .replace('{blocked}', String(blocked))
            : BRAND_KIT_DECISION_ERRORS.quality_gate_blocked,
        color: 'orange',
      });
    } else {
      published();
    }
  };

  const approveAll = (drafts: readonly AssetSetDraft[]) => {
    const blockedCount = drafts.filter((d) => !BrandKitUtils.gatePassed(d.set)).length;
    const passing = drafts.filter((d) => BrandKitUtils.gatePassed(d.set));
    modals.openConfirmModal({
      title: C.approveAllTitle,
      children: (
        <>
          <Text size="sm">
            {C.approveAllBody.replace(
              '{platforms}',
              BrandKitUtils.joinLabels(passing.map((d) => BrandKitUtils.scopeLabel(d.scope, tv))),
            )}
          </Text>
          {blockedCount > 0 && (
            <Text size="sm" c="orange" mt="xs">
              {C.approveAllBlocked.replace('{count}', String(blockedCount))}
            </Text>
          )}
        </>
      ),
      labels: { confirm: C.approveAll, cancel: 'Cancel' },
      onConfirm: () => void decideAll(drafts, 'approved'),
    });
  };

  const discardAll = (drafts: readonly AssetSetDraft[]) => {
    modals.openConfirmModal({
      title: C.discardAllTitle,
      children: <Text size="sm">{C.discardAllBody}</Text>,
      labels: { confirm: C.discardAll, cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void decideAll(drafts, 'rejected'),
    });
  };

  return { approve, override, discard, approveAll, discardAll, busy: mutation.isPending };
}
